import { useContext, useEffect, useState } from 'react';

import { OptimizelyContext, OptimizelyDecideOption, useDecision } from '@optimizely/react-sdk';

import { EXPERIMENT_RULE_KEYS, FLAG } from 'Utilities/analytics/optimizely/constants';
import useGetVehicleEnquiry from 'Utilities/vehicles/hooks/useGetVehicleEnquiry';

import { OfferPropTypes } from '../../acceptOffer/AcceptOffer.types';

import { EXPERIMENT_VARIATIONS } from './DealerRatingsExperiment.consts';
import { getDealerRatingAttributes, getIsDealerInCohort, getIsReservePriceMet } from './DealerRatingsExperiment.helpers';

export type UseDealerRatingsExperimentProps = {
  dpEnquiryId?: number;
  offer?: OfferPropTypes | null;
};

export const useDealerRatingsExperiment = ({ dpEnquiryId, offer }: UseDealerRatingsExperimentProps) => {
  const { optimizely } = useContext(OptimizelyContext);

  const [isReadyToTrackMeasuringDecision, setIsReadyToTrackMeasuringDecision] = useState(false);

  const { closingBid } = offer ?? {};
  const { dealerContact: { dealer: { id: dealerId } = {} } = {}, value: closingBidValue } = closingBid ?? {};

  const enquiryId = String(dpEnquiryId);
  const hasValidIds = Boolean(enquiryId && dealerId && dpEnquiryId);
  const isDealerInCohort = getIsDealerInCohort(dealerId);
  const isEligibleForExperiment = hasValidIds && isDealerInCohort;

  const { enquiry: { prepCosts, priceHigh, sellerWillAcceptPrice } = {} } = useGetVehicleEnquiry({
    dpEnquiryId,
  });

  const isReservePriceMet = getIsReservePriceMet({
    closingBidValue,
    prepCosts,
    priceHigh,
    sellerWillAcceptPrice,
  });

  const decisionAttributes = {
    overrideAttributes: {
      reserve_met: isReservePriceMet,
      winning_dealer_id: dealerId ?? 0,
    },
    overrideUserId: enquiryId,
  };

  const [{ enabled: isConsistencyEnabled, variationKey: consistencyDecision }] = useDecision(
    FLAG.DEALER_RATINGS_CONSISTENCY_EXPERIMENT,
    { decideOptions: isEligibleForExperiment ? [] : [OptimizelyDecideOption.DISABLE_DECISION_EVENT] },
    {
      ...decisionAttributes,
    },
  );

  const shouldForceTestVariations = Boolean(isEligibleForExperiment && isConsistencyEnabled && consistencyDecision);

  useEffect(() => {
    if (consistencyDecision && shouldForceTestVariations && optimizely) {
      optimizely.onReady().then(() => {
        Object.values(EXPERIMENT_RULE_KEYS.DEALER_RATINGS_MEASURING_EXPERIMENT)
          .forEach((ruleKey) => optimizely.setForcedVariation(ruleKey, enquiryId, consistencyDecision));

        setIsReadyToTrackMeasuringDecision(true);
      });
    }
  }, [consistencyDecision, optimizely, shouldForceTestVariations, enquiryId]);

  const [{ ruleKey, variationKey: measuringDecision }] = useDecision(
    FLAG.DEALER_RATINGS_MEASURING_EXPERIMENT,
    { decideOptions: isReadyToTrackMeasuringDecision ? [] : [OptimizelyDecideOption.DISABLE_DECISION_EVENT] },
    {
      ...decisionAttributes,
    },
  );

  const isConsistencyAndMeasuringDecisionMismatch = consistencyDecision !== measuringDecision;

  useEffect(() => {
    if (isReadyToTrackMeasuringDecision && isConsistencyAndMeasuringDecisionMismatch) {
      const message = 'Mismatch between the dealer ratings consistency and measuring experiments, check the Optimizely flag setup.';

      globalThis?.Sentry?.captureException(new Error(message), {
        level: 'warning',
      });
    }
  }, [isReadyToTrackMeasuringDecision, isConsistencyAndMeasuringDecisionMismatch]);

  const shouldDisplayDealerRating = ruleKey === EXPERIMENT_RULE_KEYS.DEALER_RATINGS_MEASURING_EXPERIMENT.AB_TEST_ROLLOUT
    && measuringDecision === EXPERIMENT_VARIATIONS.TEST
    && isReadyToTrackMeasuringDecision;

  return {
    dealerRating: shouldDisplayDealerRating ? getDealerRatingAttributes(dealerId) : undefined,
    isEligibleForExperiment,
  };
};
